// component/DeleteDepartmentModal.js
import React, { useState, useEffect } from "react";
import {
  Modal,
  Button,
  Input,
  Space,
  Typography,
  Alert,
  Progress,
  Tag,
  Avatar,
  message,
} from "antd";
import {
  DeleteOutlined,
  WarningOutlined,
  CheckCircleOutlined,
  CloseOutlined,
  ExclamationCircleOutlined,
  ApartmentOutlined,
} from "@ant-design/icons";
import { MdOutlineWarning } from "react-icons/md";

const { Text, Title } = Typography;

const DeleteDepartmentModal = ({
  visible,
  onCancel,
  onConfirm,
  departmentName,
  departmentCode,
  departmentId,
  parentDept,
  loading = false,
}) => {
  const [inputDelete, setInputDelete] = useState("");
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [shake, setShake] = useState(false);

  useEffect(() => {
    if (!visible) {
      setInputDelete("");
      setIsConfirmed(false);
    }
  }, [visible]);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputDelete(value);
    setIsConfirmed(value === "DELETE");
    
    // Shake animation for wrong input
    if (value && value !== "DELETE" && value.length > 3) {
      setShake(true);
      setTimeout(() => setShake(false), 500);
    }
  };

  const handleConfirm = () => {
    if (isConfirmed) {
      onConfirm();
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && isConfirmed) {
      handleConfirm();
    }
  };

  return (
    <Modal
      title={
        <div style={styles.modalTitle}>
          <div style={styles.iconWrapper}>
            <DeleteOutlined style={styles.deleteIcon} />
          </div>
          <span style={styles.titleText}>លុបផ្នែក</span>
        </div>
      }
      open={visible}
      onCancel={onCancel}
      width={520}
      centered
      closable={!loading}
      maskClosable={!loading}
      footer={null}
      className="delete-department-modal"
    >
      <div style={styles.content}>
        {/* Warning Alert */}
        <Alert
          message={
            <span style={{ color: "#ef4444", fontWeight: 600 }}>
              ការព្រមាន / Warning
            </span>
          }
          description={
            <span style={{ color: "#ef4444" }}>
              ការលុបនេះមិនអាចត្រឡប់វិញបានទេ។ 
              ទិន្នន័យទាំងអស់របស់ផ្នែកនឹងត្រូវលុបចោលជាអចិន្ត្រៃយ៍។
              <br />
              <strong>This action cannot be undone. All department data will be permanently removed.</strong>
            </span>
          }
          type="error"
          showIcon
          icon={<ExclamationCircleOutlined />}
          style={styles.alert}
        />

        {/* Department Info Card */}
        <div style={styles.departmentCard}>
          <div style={styles.departmentAvatar}>
            <ApartmentOutlined style={styles.avatarIcon} />
          </div>
          <div style={styles.departmentInfo}>
            <Text style={styles.departmentLabel}>ផ្នែកដែលត្រូវលុប / Department to delete</Text>
            <Title level={4} style={styles.departmentName}>
              {departmentName || "N/A"}
            </Title>
            <Space size={8}>
              <Tag color="blue" style={styles.codeTag}>
                លេខកូដ: {departmentCode || "N/A"}
              </Tag>
              {parentDept && (
                <Tag color="geekblue" style={styles.parentTag}>
                  ស្ថិតក្រោម: {parentDept}
                </Tag>
              )}
            </Space>
          </div>
        </div>

        {/* Warning about related data */}
        <div style={styles.warningBox}>
          <WarningOutlined style={{ color: "#faad14", fontSize: 16, marginRight: 8 }} />
          <Text style={styles.warningText}>
            ប្រសិនបើផ្នែកនេះមានផ្នែករង ឬបុគ្គលិកភ្ជាប់, ពួកវាក៏នឹងរងផលប៉ះពាល់ដែរ។
            <br />
            <small>If this department has sub-departments or associated staff, they will also be affected.</small>
          </Text>
        </div>

        {/* Confirmation Section */}
        <div style={styles.confirmationSection}>
          <div style={styles.confirmationHeader}>
            <MdOutlineWarning style={styles.warningIcon} />
            <Text strong style={styles.confirmationText}>
              ដើម្បីបញ្ជាក់ការលុប សូមបញ្ចូលពាក្យ "DELETE"
              <br />
              <small>To confirm deletion, please type "DELETE"</small>
            </Text>
          </div>

          <Input
            value={inputDelete}
            onChange={handleInputChange}
            onKeyPress={handleKeyPress}
            placeholder="Type DELETE to confirm / បញ្ចូលពាក្យ DELETE ដើម្បីបញ្ជាក់"
            disabled={loading}
            autoFocus
            style={{
              ...styles.confirmInput,
              ...(inputDelete === "DELETE" ? styles.inputSuccess : {}),
              ...(shake ? styles.inputShake : {}),
            }}
            prefix={
              inputDelete === "DELETE" ? (
                <CheckCircleOutlined style={{ color: "#52c41a", fontSize: 18 }} />
              ) : (
                <WarningOutlined style={{ color: "#faad14", fontSize: 18 }} />
              )
            }
          />

          {/* Validation Feedback */}
          {inputDelete && inputDelete !== "DELETE" && (
            <div style={styles.errorFeedback}>
              <CloseOutlined style={{ color: "#ff4d4f", fontSize: 14 }} />
              <Text style={styles.errorText}>
                សូមបញ្ចូលពាក្យ "DELETE" ឲ្យបានត្រឹមត្រូវ / Please type "DELETE" correctly
              </Text>
            </div>
          )}

          {inputDelete === "DELETE" && (
            <div style={styles.successFeedback}>
              <CheckCircleOutlined style={{ color: "#52c41a", fontSize: 14 }} />
              <Text style={styles.successText}>
                បានបញ្ជាក់។ អ្នកអាចលុបបាន។ / Confirmed. You can now delete.
              </Text>
            </div>
          )}

          {/* Progress Indicator */}
          {inputDelete && inputDelete !== "DELETE" && (
            <Progress
              percent={Math.min((inputDelete.length / 6) * 100, 100)}
              showInfo={false}
              strokeColor="#ff4d4f"
              trailColor="#f0f0f0"
              size="small"
              style={styles.progress}
            />
          )}
        </div>

        {/* Action Buttons */}
        <div style={styles.actionButtons}>
          <Button
            size="large"
            onClick={onCancel}
            disabled={loading}
            style={styles.cancelButton}
          >
            បោះបង់ / Cancel
          </Button>
          <Button
            type="primary"
            size="large"
            onClick={handleConfirm}
            disabled={!isConfirmed || loading}
            loading={loading}
            danger
            style={{
              ...styles.confirmButton,
              ...(isConfirmed && !loading ? styles.confirmButtonActive : {}),
            }}
          >
            {loading ? "កំពុងលុប..." : "លុប / Delete"}
          </Button>
        </div>

        {/* Footer Note */}
        <div style={styles.footerNote}>
          <Text type="secondary" style={styles.noteText}>
            <MdOutlineWarning style={{ fontSize: 12, marginRight: 4 }} />
            សកម្មភាពនេះមិនអាចត្រឡប់វិញបានទេ / This action cannot be undone
          </Text>
        </div>
      </div>

      <style>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-5px); }
          75% { transform: translateX(5px); }
        }
        
        .delete-department-modal .ant-modal-content {
          border-radius: 20px;
          overflow: hidden;
          font-family: 'Content', "Poppins", system-ui, sans-serif;
        }
        
        .delete-department-modal .ant-modal-header {
          padding: 20px 24px;
          border-bottom: 1px solid #f0f0f0;
        }
        
        .delete-department-modal .ant-modal-body {
          padding: 24px;
        }
        
        .delete-department-modal .ant-modal-close {
          top: 20px;
          right: 20px;
        }
        
        .delete-department-modal .ant-btn {
          font-family: 'Content', "Poppins", system-ui, sans-serif;
        }
        
        .delete-department-modal .ant-alert {
          font-family: 'Content', "Poppins", system-ui, sans-serif;
        }
        
        .delete-department-modal .ant-tag {
          font-family: 'Content', "Poppins", system-ui, sans-serif;
        }
        
        .delete-department-modal .ant-input {
          font-family: 'Content', "Poppins", system-ui, sans-serif;
        }
        
        .delete-department-modal .ant-progress-text {
          font-family: 'Content', "Poppins", system-ui, sans-serif;
        }
        
        .delete-department-modal .ant-typography {
          font-family: 'Content', "Poppins", system-ui, sans-serif;
        }
      `}</style>
    </Modal>
  );
};

const styles = {
  modalTitle: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
  },
  iconWrapper: {
    width: "36px",
    height: "36px",
    borderRadius: "50%",
    background: "#FEF2F2",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  deleteIcon: {
    fontSize: "18px",
    color: "#ef4444",
  },
  titleText: {
    fontSize: "18px",
    fontWeight: 600,
    color: "#1F2937",
  },
  content: {
    padding: "8px 0",
  },
  alert: {
    marginBottom: "20px",
    borderRadius: "12px",
    border: "1px solid #FEE2E2",
    backgroundColor: "#FEF2F2",
  },
  departmentCard: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
    padding: "16px",
    backgroundColor: "#F9FAFB",
    borderRadius: "16px",
    marginBottom: "20px",
    border: "1px solid #E5E7EB",
  },
  departmentAvatar: {
    width: "56px",
    height: "56px",
    borderRadius: "50%",
    background: "linear-gradient(135deg, #FEE2E2 0%, #FECACA 100%)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  avatarIcon: {
    fontSize: "28px",
    color: "#ef4444",
  },
  departmentInfo: {
    flex: 1,
  },
  departmentLabel: {
    fontSize: "11px",
    color: "#6B7280",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  departmentName: {
    margin: "4px 0 4px 0",
    fontSize: "18px",
    fontWeight: 700,
    color: "#1F2937",
  },
  codeTag: {
    borderRadius: "6px",
    fontSize: "11px",
  },
  parentTag: {
    borderRadius: "6px",
    fontSize: "11px",
  },
  warningBox: {
    backgroundColor: "#FFFBEB",
    border: "1px solid #FDE68A",
    borderRadius: "12px",
    padding: "12px 16px",
    marginBottom: "20px",
    display: "flex",
    alignItems: "flex-start",
  },
  warningText: {
    fontSize: "12px",
    color: "#92400E",
    flex: 1,
  },
  confirmationSection: {
    marginBottom: "24px",
  },
  confirmationHeader: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    marginBottom: "12px",
  },
  warningIcon: {
    fontSize: "18px",
    color: "#faad14",
  },
  confirmationText: {
    fontSize: "13px",
    color: "#92400E",
    textAlign: "center",
  },
  confirmInput: {
    height: "48px",
    borderRadius: "12px",
    fontSize: "14px",
    fontWeight: 500,
    letterSpacing: "0.5px",
    transition: "all 0.3s ease",
    textAlign: "center",
  },
  inputSuccess: {
    borderColor: "#52c41a",
    backgroundColor: "#F6FFED",
  },
  inputShake: {
    animation: "shake 0.5s ease-in-out",
  },
  errorFeedback: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    marginTop: "8px",
    padding: "6px 12px",
    backgroundColor: "#FEF2F2",
    borderRadius: "8px",
  },
  errorText: {
    fontSize: "12px",
    color: "#ff4d4f",
  },
  successFeedback: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    marginTop: "8px",
    padding: "6px 12px",
    backgroundColor: "#F6FFED",
    borderRadius: "8px",
  },
  successText: {
    fontSize: "12px",
    color: "#52c41a",
  },
  progress: {
    marginTop: "12px",
  },
  actionButtons: {
    display: "flex",
    gap: "12px",
    marginBottom: "16px",
  },
  cancelButton: {
    flex: 1,
    height: "44px",
    borderRadius: "12px",
    fontSize: "14px",
    fontWeight: 500,
    borderColor: "#E5E7EB",
    color: "#6B7280",
  },
  confirmButton: {
    flex: 1,
    height: "44px",
    borderRadius: "12px",
    fontSize: "14px",
    fontWeight: 600,
    backgroundColor: "#E5E7EB",
    borderColor: "#E5E7EB",
    color: "#9CA3AF",
  },
  confirmButtonActive: {
    backgroundColor: "#ef4444",
    borderColor: "#ef4444",
    color: "white",
    boxShadow: "0 4px 12px rgba(239, 68, 68, 0.3)",
  },
  footerNote: {
    textAlign: "center",
    paddingTop: "12px",
    borderTop: "1px solid #F3F4F6",
  },
  noteText: {
    fontSize: "11px",
    color: "#9CA3AF",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "4px",
  },
};

export default DeleteDepartmentModal;